import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { forgotPassword, sendForgotPasswordVerifyCode } from '../../api';
import { App, Tooltip } from 'antd';
import InputField from '../Form/InputField';
import Button from '../Form/Button';
import { validatePassword } from '../../utils/tools.js';
import { ERROR_MESSAGES } from '../../utils/constants';
import icon from '@/utils/icon.js';

const ForgotPasswordBox = ({ onSuccess, onSwitchToLogin, showCloseBtn = false, onClose }) => {
  const { message } = App.useApp();
  const [formData, setFormData] = useState({
    phone: '',
    verifyCode: '',
    psw: '',
    confirmPsw: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.phone) {
      setError('请输入手机号');
      setLoading(false);
      return;
    }

    if (!formData.verifyCode) {
      setError('请输入验证码');
      setLoading(false);
      return;
    }

    if (!formData.psw) {
      setError('请输入新密码');
      setLoading(false);
      return;
    }

    const pwdCheck = validatePassword(formData.psw);
    if (!pwdCheck.ok) {
      setError(pwdCheck.msg);
      setLoading(false);
      return;
    }

    if (formData.psw !== formData.confirmPsw) {
      setError('两次输入的密码不一致');
      setLoading(false);
      return;
    }

    try {
      const result = await forgotPassword(formData.phone, formData.psw, formData.verifyCode);
      if (result.errorCode === 0) {
        message.success('密码重置成功，请重新登录');
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/login');
        }
      } else {
        setError(result.errorMsg || '重置失败');
      }
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!formData.phone) {
      setError('请输入手机号');
      return;
    }

    setSendingCode(true);
    try {
      const result = await sendForgotPasswordVerifyCode(formData.phone);
      if (result.errorCode === 0 || result.errorCode === "0") {
        message.success('验证码发送成功');
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(result.errorMsg || result.message || '验证码发送失败');
      }
    } catch (err) {
      console.error('发送验证码失败:', err);
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setSendingCode(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
    setError('');
  };

  return (
    <div className="relative w-full max-w-sm bg-[#1A1C1B] border border-white/20 rounded-xl p-8 text-white pt-[24px]">
      <img src={icon.moldAISvg2} className='h-9'></img>
      {showCloseBtn && (
        <button
          onClick={onClose}
          className="absolute top-7 right-6 text-white/40 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <h1 className="text-2xl font-medium mb-8">重置你的密码</h1>

      <form className="relative space-y-6" onSubmit={handleSubmit}>
        <div className="relative">
          <InputField
            type="tel"
            placeholder="手机号"
            id="phone"
            name="phone"
            autoComplete="tel"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="relative">
          <InputField
            type="text"
            placeholder="验证码"
            id="verifyCode"
            name="verifyCode"
            autoComplete="one-time-code"
            value={formData.verifyCode}
            onChange={handleChange}
            className="pr-24"
          />
          <button
            type="button"
            className="absolute right-0 top-0 h-full px-4 text-sm text-[#61EA3C] disabled:text-gray-500"
            onClick={handleSendCode}
            disabled={countdown > 0 || sendingCode}
          >
            {countdown > 0 ? `${countdown}s` : sendingCode ? '发送中...' : '发送验证码'}
          </button>
        </div>

        <Tooltip
          title="密码长度 8-14 位，可包含字母、数字和英文标点，不支持空格、中文；字母/数字以及标点符号至少包含2种"
          placement="topLeft"
          trigger={['focus']}
          getPopupContainer={(trigger) => trigger.parentElement}
          overlayStyle={{ width: '100%', maxWidth: 'none' }}
        >
          <InputField
            type="password"
            placeholder="新密码"
            id="psw"
            name="newPassword"
            autoComplete="new-password"
            value={formData.psw}
            onChange={handleChange}
          />
        </Tooltip>

        <InputField
          type="password"
          placeholder="确认新密码"
          id="confirmPsw"
          name="confirmPassword"
          autoComplete="new-password"
          value={formData.confirmPsw}
          onChange={handleChange}
        />

        {error && (
          <span className="login_error text-left text-sm text-rose-500 block">
            {error}
          </span>
        )}

        <Button type="submit" disabled={loading}>
          {loading ? '重置中...' : '重置密码'}
        </Button>
      </form>

      <div className="text-center text-sm text-gray-400 mt-6">
        {onSwitchToLogin ? (
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-[#61EA3C] hover:underline"
          >
            返回登录
          </button>
        ) : (
          <Link to="/login" className="text-[#61EA3C] hover:underline">
            返回登录
          </Link>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordBox;
